import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17390 — one license push the client license export made to an ERP: the
 * record behind Compliance > Sent to ERP.
 *
 * Written by mover-integration's `client-license-export` job through the
 * platform object API (`object/license_export_log`), one row per license per
 * run, for Blue Link (Republic), NetSuite (Pharmsource) and DOit (HHC). The
 * database sync creates the table and its `_object` / `_object_field` rows,
 * which is what lets the integration write it. The CRM only reads it and
 * records acknowledgements.
 *
 * `result` is the ERP's answer or Workd's own word for what happened (see
 * LICENSE_EXPORT_RESULTS in libs/shared/src/utils/license-export-log.ts).
 * `needsAcknowledgement` is decided by the integration when it writes the row
 * (the ERP's data changed, or the push failed) and never changes afterwards.
 * A failure retried on every run updates its own row (`attemptCount`,
 * `lastAttemptAt`) instead of adding one, while nobody has acknowledged it.
 *
 * No foreign keys on purpose: the log is history and must outlive the client,
 * address, license and user rows it names. A plain varchar for `result` so a
 * new answer is an additive change the integration can write without a
 * migration.
 *
 * SM1 exposure: none — a new table SM1 never names.
 */
@Index('idx_lel_client', ['fkClientId', 'runStartedAt'], {})
@Index('idx_lel_run', ['runStartedAt'], {})
@Index('idx_lel_ack', ['needsAcknowledgement', 'acknowledgedAt'], {})
@Index('idx_lel_license', ['fkClientLicenseId'], {})
@Entity('license_export_log', {}, { label: 'License Export Log' })
export class LicenseExportLogEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** The ship-to the license belongs to. */
	@Column('int', {
		name: 'fk_client_address__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientAddressId!: number | null;

	@Column('int', {
		name: 'fk_client_license__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientLicenseId!: number | null;

	/** 'Blue Link', 'NetSuite' or 'DOit'. */
	@Column('varchar', { name: 'erpName', length: 50 })
	public erpName!: string;

	/** When the export run started; every row of one run shares it. */
	@Column('datetime', { name: 'runStartedAt' })
	public runStartedAt!: Date;

	/** The Workd license type (State Board, DEA, ...). */
	@Column('varchar', { name: 'licenseType', length: 255, nullable: true })
	public licenseType!: string | null;

	@Column('varchar', { name: 'state', length: 10, nullable: true })
	public state!: string | null;

	@Column('varchar', { name: 'licenseNumber', length: 255, nullable: true })
	public licenseNumber!: string | null;

	@Column('date', { name: 'expirationDateSent', nullable: true })
	public expirationDateSent!: string | null;

	/**
	 * What the ERP held before the write, where the ERP says so (Blue Link,
	 * whose licenses are read just before writing). All three NULL for a
	 * license the ERP did not have, and for NetSuite and DOit.
	 */
	@Column('varchar', {
		name: 'previousLicenseNumber',
		length: 255,
		nullable: true,
	})
	public previousLicenseNumber!: string | null;

	@Column('date', { name: 'previousExpirationDate', nullable: true })
	public previousExpirationDate!: string | null;

	@Column('varchar', { name: 'previousState', length: 10, nullable: true })
	public previousState!: string | null;

	@Column('varchar', { name: 'result', length: 50 })
	public result!: string;

	/** The ERP's message, or Workd's reason for a skip or a failure. */
	@Column('text', { name: 'resultMessage', nullable: true })
	public resultMessage!: string | null;

	/** The ERP's own id for the license row it wrote, where it returns one. */
	@Column('varchar', { name: 'erpLicenseId', length: 100, nullable: true })
	public erpLicenseId!: string | null;

	@Column('tinyint', {
		name: 'needsAcknowledgement',
		width: 1,
		default: 0,
		transformer: {
			to: (value: boolean | null | undefined): number =>
				value === true ? 1 : 0,
			from: (value: number | null): boolean => value === 1,
		},
	})
	public needsAcknowledgement!: boolean;

	/** How many runs reported this same outcome (1 for most rows). */
	@Column('int', { name: 'attemptCount', unsigned: true, default: 1 })
	public attemptCount!: number;

	@Column('datetime', { name: 'lastAttemptAt', nullable: true })
	public lastAttemptAt!: Date | null;

	@Column('datetime', { name: 'acknowledgedAt', nullable: true })
	public acknowledgedAt!: Date | null;

	/** Tenant `user._id` of the person who acknowledged the row. */
	@Column('int', {
		name: 'fk_user__id_acknowledged',
		unsigned: true,
		nullable: true,
	})
	public fkUserIdAcknowledged!: number | null;

	@Column('text', { name: 'acknowledgementNote', nullable: true })
	public acknowledgementNote!: string | null;
}
